import {clearGroup} from "../../../sceneManager";
import {buildingGroup, roomNamesGroup} from "../../../main";
import {supportBeamMeshName, wallGroupName} from "../../../constants/nameConstants";
import {Wall} from "./models/wall";
import {SupportBeam} from "./models/supportBeam";
import {getIntersectingWalls} from "../../walls/wall";
import {CombinedBoundary} from "./models/combinedBoundary";
import {
    Box3,
    BufferAttribute,
    Color,
    DoubleSide,
    FrontSide,
    Matrix4,
    MeshStandardMaterial,
    ShapeUtils,
    Vector2,
    Vector3
} from "three";
import {HBEWallDefaultThickness, isDebug} from "../../../globals";
import {addNumberedDefaultCirclePointer} from "../../../utils/pointerUtils";
import {toFixedFloat, toFixedVector} from "../../../utils/utils";
import {Node} from "./models/node";
import {createMeshFromShape, createShapeFromPoints, pathPointsClockwise} from "../../../utils/shapeUtils";
import {extract_cycles} from './cyclesAlgorythm';
import {roomMaterial} from "../../../constants/materialConstants";

export function getRooms() {
    let buildingElements = getBuildingElements();
    let combinedBuildingElements = combineBuildingElements(buildingElements);
    // console.log(combinedBuildingElements);

    let nodes = findBuildingElementNodes(combinedBuildingElements);
    /*console.log(nodes);
    nodes.forEach(node => {
        console.log("Node " + node.identifier + " point: (" + node.point.x + " ; " + node.point.z + ")");
    });*/

    let connectedNodes = createNodeConnections(nodes);
    /*connectedNodes.forEach(node => {
        const identifiers = node.connectedNodes.map(innerNode => `${innerNode.identifier}`);
        console.log("Node (" + node.identifier + ") is connected with: " + identifiers.join(", "));
    });*/

    debugDrawNodes(nodes);

    const loops = findLoops(connectedNodes);
    /*console.log(loops);
    loops.forEach((cycle, index) => {
        const points = cycle.map(node => `${node.identifier}`);
        console.log(`Cycle ${index + 1}: [${points.join(', ')}]`);
    });*/
    return createRoomMeshes(loops);
}

//CREATING BUILDINGELEMENTS AND NODES
/**
 * Alle relevanten Gebäude Elemente (Wände/Unterzüge) aus der Szene laden.
 * Elemente die, die Verlängerung eines anderen Elements sind, werden dabei in einem zusammengefasst und später als eins behandelt.
 * @returns {*[]}
 */
function getBuildingElements() {
    let buildingElements = [];

    //Alle Elemente aus der buildingGroup iterieren
    for (let i = 0; i < buildingGroup.children.length; i++) {
        let element = buildingGroup.children[i];

        //Element für eine Wand oder Unterzug erzeugen
        if (element.name === wallGroupName) {
            buildingElements.push(new Wall(element));
        } else if (element.name === supportBeamMeshName) {
            buildingElements.push(new SupportBeam(element));
        }
    }

    return buildingElements;
}

/**
 * Kombiniert alle Elemente, die eine Erweiterung eines anderen Elements sind.
 *
 * Aufteilung der Elemente in X und Z ausgerichtete Elemente. Nur gleich orientierte Elemente können sich gegenseitig extended.
 * Daher brauchen wir diese eh nicht miteinander abgleichen.
 * Durch die Aufteilung können wir die Elemente nach ihrer Position sortieren. Wodurch sichergestellt werden kann, dass die Reihenfolge so liegt,
 * dass Elemente die zusammengehören auch nacheinander in der Liste vorkommen.
 * Ohne Sortierung kann es passieren, dass Elemente nachher getrennt bleiben, die aber zusammengehören.
 *
 * @param buildingElements
 * @returns {*[]}
 */
function combineBuildingElements(buildingElements) {
    let buildingElementsX = buildingElements.filter(x => x.orientation === "x").sort(compareByX).sort(compareByZ);
    let buildingElementsZ = buildingElements.filter(x => x.orientation === "z").sort(compareByZ).sort(compareByX);

    let combinedBuildingElementsX = combineBuildingElementsByOrientation(buildingElementsX);
    let combinedBuildingElementsZ = combineBuildingElementsByOrientation(buildingElementsZ);
    return [...combinedBuildingElementsX, ...combinedBuildingElementsZ];
}

function compareByX(a, b) {
    return a.boundingBox.min.x - b.boundingBox.min.x;
}

function compareByZ(a, b) {
    return a.boundingBox.min.z - b.boundingBox.min.z;
}

function combineBuildingElementsByOrientation(buildingElements) {
    let combinedBuildingElements = [];

    for (let i = 0; i < buildingElements.length; i++) {
        let boundaryElement = buildingElements[i];
        //Prüfen, ob das Element die Erweiterung eines bereits gefundenen Elements ist.
        let newBuildingElements = extendsOtherBoundaryElement(combinedBuildingElements, boundaryElement);
        if (newBuildingElements === null) {
            combinedBuildingElements.push(boundaryElement); //Keine Verlängerung. Dann als neues Element hinzufügen.
        } else {
            //Wenn es eine Verlängerung ist, wird das Element in extendsOtherBoundaryElement angepasst und das angepasste Array zurückgegeben.
            combinedBuildingElements = newBuildingElements;
        }
    }

    return combinedBuildingElements;
}

function extendsOtherBoundaryElement(boundaries, newBoundary) {
    for (let i = 0; i < boundaries.length; i++) {
        let existingBoundary = boundaries[i];

        //Nur prüfen, wenn die beiden Elemente in die gleiche Richtung verlaufen. Sonst kann es keine Verlängerung sein.
        if (existingBoundary.orientation === newBoundary.orientation) {
            //Wenn das existierende Element bereits eine CombinedBoundary ist. Dann kann diese einfach nur erweitert werden.
            if (existingBoundary instanceof CombinedBoundary) {
                //Prüfen, ob das neue Element eins der bereits enthaltenen SceneObjects erweitert.
                let result = getIntersectingWalls(newBoundary.sceneObject, existingBoundary.sceneObjects, true, false, false, true, true); //Geschnittene Wände bestimmen
                if (result.length > 0) {
                    //Das neue Element in die CombinedBoundary aufnehmen.
                    boundaries[i].sceneObjects.push(newBoundary.sceneObject);
                    boundaries[i].boundingBox = getCombinedBoundingBoxes(newBoundary.orientation, boundaries[i].boundingBox, newBoundary.boundingBox);
                    boundaries[i].originalBoundingBox = getCombinedBoundingBoxes(newBoundary.orientation, boundaries[i].originalBoundingBox, newBoundary.originalBoundingBox);
                    return boundaries;
                }
            } else {
                //Wenn nicht, muss aus den beiden Elementen eine CombinedBoundary erzeugt werden und das Element im Array ersetzt werden

                //Prüfen, ob das neue Element eins das bereits enthaltene SceneObject erweitert.
                let result = getIntersectingWalls(newBoundary.sceneObject, [existingBoundary.sceneObject], true, false, false, true, true); //Geschnittene Wände bestimmen

                if (result.length > 0) {
                    //Aus den beiden Elementen eine CombinedBoundary erzeugen.
                    let combinedBBox = boundaries[i].boundingBox = getCombinedBoundingBoxes(newBoundary.orientation, boundaries[i].boundingBox, newBoundary.boundingBox);
                    let combinedOriginalBBox = boundaries[i].originalBoundingBox = getCombinedBoundingBoxes(newBoundary.orientation, boundaries[i].originalBoundingBox, newBoundary.originalBoundingBox);
                    let combinedSceneObjects = [boundaries[i].sceneObject, newBoundary.sceneObject];
                    boundaries[i] = new CombinedBoundary(combinedSceneObjects, existingBoundary.orientation, combinedBBox, combinedOriginalBBox);
                    return boundaries;
                }
            }
        }
    }

    return null; //Element kann mit keinem bestehenden kombiniert werden.
}

/**
 * BoundingBoxen können nicht einfach direkt per Union verbunden werden. Bei Unterzügen mit Breite >10cm führt dies zu falschen Punkten.
 * Daher werden diese so verarbeitet, dass wenn diese mit einer Wand verbunden sind die Breite des schmaleren Elements als min/max Wert für die Breite genutzt werden.
 * @param orientation
 * @param boundingBox
 * @param newElementBoundingBox
 * @returns {Box3}
 */
function getCombinedBoundingBoxes(orientation, boundingBox, newElementBoundingBox) {
    let newBounds = new Box3();
    let existingElementSize = boundingBox.getSize(new Vector3());
    let newElementSize = newElementBoundingBox.getSize(new Vector3());

    let boundWidthEqual = true;
    if (orientation === "x") {
        boundWidthEqual = existingElementSize.z === newElementSize.z;
    } else {
        boundWidthEqual = existingElementSize.x === newElementSize.x;
    }

    newBounds.copy(boundingBox).union(newElementBoundingBox);

    //Wenn die Breiten gleich sind ist keine weitere Behandlung notwendig.
    if (boundWidthEqual) {
        return newBounds;
    }

    //Für unterschiedlich Breite BoundingBoxen müssen diese auf die schmallere getrimmt werden.
    if (orientation === "x") {
        //Schmallere Bounds min/max Werte übernehmen
        if (existingElementSize.z >= newElementSize.z) {
            newBounds.min.z = newElementBoundingBox.min.z;
            newBounds.max.z = newElementBoundingBox.max.z;
        } else {
            newBounds.min.z = boundingBox.min.z;
            newBounds.max.z = boundingBox.max.z;
        }
    } else {
        //Schmallere Bounds min/max Werte übernehmen
        if (existingElementSize.x >= newElementSize.x) {
            newBounds.min.x = newElementBoundingBox.min.x;
            newBounds.max.x = newElementBoundingBox.max.x;
        } else {
            newBounds.min.x = boundingBox.min.x;
            newBounds.max.x = boundingBox.max.x;
        }
    }

    return newBounds;
}

/**
 * Auslesen aller Nodes/Schnittpunkte. Auslesen welche Elemente an diesen Node connecten.
 * @param buildingElements
 * @returns {*[]}
 */
function findBuildingElementNodes(buildingElements) {
    let nodes = [];

    // Iterate through each pair of building elements
    for (let i = 0; i < buildingElements.length - 1; i++) {
        for (let j = i + 1; j < buildingElements.length; j++) {
            const box1 = buildingElements[i].boundingBox;
            const box2 = buildingElements[j].boundingBox;

            // Check for node
            if (box1.intersectsBox(box2)) {
                // Calculate the node bounding box
                const nodeBox = new Box3();
                nodeBox.copy(box1).intersect(box2);

                let size = nodeBox.getSize(new Vector3());

                let firstCheck = toFixedFloat(size.x) >= HBEWallDefaultThickness && toFixedFloat(size.z) >= HBEWallDefaultThickness && buildingElements[i].orientation !== buildingElements[j].orientation;
                let secondCheck = ((toFixedFloat(size.x) >= HBEWallDefaultThickness * 2 && toFixedFloat(size.z) >= HBEWallDefaultThickness) || (toFixedFloat(size.x) >= HBEWallDefaultThickness && toFixedFloat(size.z) >= HBEWallDefaultThickness * 2)) && buildingElements[i].orientation === buildingElements[j].orientation;

                if (firstCheck || secondCheck) {
                    //Nodepunkt berechnen. (Center der Schnittbox)
                    let nodeCenter = new Vector3();
                    nodeCenter.addVectors(nodeBox.min, nodeBox.max);
                    nodeCenter.multiplyScalar(0.5);
                    nodeCenter = toFixedVector(nodeCenter);

                    //Prüfen ob bereits ein Node mit dem gleichen Punkt existiert. Wenn nicht neuen erstellen. Sonst die Elemente bei Bedarf den connectedBuildElements hinzufügen.
                    let nodesContainsSamePoint = nodes.filter(x => x.point.x === nodeCenter.x && x.point.z === nodeCenter.z);
                    if (nodesContainsSamePoint.length === 0) {
                        let node = new Node(
                            nodeCenter,
                            [buildingElements[i], buildingElements[j]],
                            nodes.length + 1
                        );

                        nodes.push(node);
                    } else {
                        if (!nodesContainsSamePoint[0].connectedBuildingElements.some(x => x === buildingElements[i])) {
                            nodesContainsSamePoint[0].connectedBuildingElements.push(buildingElements[i]);
                        }
                        if (!nodesContainsSamePoint[0].connectedBuildingElements.some(x => x === buildingElements[j])) {
                            nodesContainsSamePoint[0].connectedBuildingElements.push(buildingElements[j]);
                        }
                    }
                }
            }
        }
    }

    return nodes;
}

//GRAPH BUILDING
function createNodeConnections(nodes) {
    // Iterate through each pair of node points
    for (let i = 0; i < nodes.length - 1; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
            const node1 = nodes[i];
            const node2 = nodes[j];

            let isDirect = isDirectConnection(node1, node2, nodes);
            if (isDirect === true) {
                nodes[i].connectedNodes.push(nodes[j]);
                nodes[j].connectedNodes.push(nodes[i]);
            }
        }
    }

    return nodes;
}

function isDirectConnection(node1, node2, nodes) {
    // Check if any other node points are between point1 and point2 along their bounding boxes
    const otherNodes = nodes.filter(node => (
        node !== node1 &&
        node !== node2
    ));

    for (let i = 0; i < otherNodes.length; i++) {
        let node = otherNodes[i];
        /*TODO Das funktioniert noch nicht, wenn man breitere Unterzüge nutzt dann liegt der Punkt minimal off und wird dann nicht als draufliegend erkannt*/
        if (shareWallConnection(node, node1) || shareWallConnection(node, node2)) {
            if (isBetweenPoints(node1.point, node2.point, node.point)) {
                return node;
            }
        }
    }

    return shareWallConnection(node1, node2);
}

function isBetweenPoints(pointA, pointB, pointToCheck) {
    if (!isPointOnLine(pointA, pointB, pointToCheck)) {
        return false;
    }

    let dx = pointB.x - pointA.x;
    let dy = pointB.z - pointA.z;

    // if a line is a more horizontal than vertical:
    if (Math.abs(dx) >= Math.abs(dy)) {
        if (dx > 0) {
            return pointA.x <= pointToCheck.x && pointToCheck.x <= pointB.x;
        } else {
            return pointB.x <= pointToCheck.x && pointToCheck.x <= pointA.x;
        }
    } else {
        if (dy > 0) {
            return pointA.z <= pointToCheck.z && pointToCheck.z <= pointB.z;
        } else {
            return pointB.z <= pointToCheck.z && pointToCheck.z <= pointA.z;
        }
    }
}

function isPointOnLine(pointA, pointB, pointToCheck) {
    let c = new Vector3();
    c.crossVectors(pointA.clone().sub(pointToCheck), pointB.clone().sub(pointToCheck));
    return c.length() <= 0.0001;
}

function shareWallConnection(node1, node2) {
    let elements1 = node1.connectedBuildingElements;
    let elements2 = node2.connectedBuildingElements;
    return elements1.some(obj1 => elements2.includes(obj1));
}


//LOOP CALCULATION
/**
 * Alle Loops in den verbundenen Nodes suchen.
 * Hierfür wird ein Algorithmus von jemandem genutzt.
 * Dafür müssen die Daten vorher aber einmal entsprechend angepasst werden
 * und das Resultat später entsprechend interpretiert werden.
 * @param nodes
 * @returns {*[]}
 */
function findLoops(nodes) {
    let verticies = transferToVerticies(nodes);
    let result = extract_cycles(verticies);
    let loops = transferBackToNodes(nodes, result);

    return loops;
}

function transferToVerticies(nodes) {
    let verticies = [];

    nodes.forEach(node => {
        verticies.push({
            x: node.point.x,
            y: node.point.z,
            identifier: node.identifier,
            adj: []
        });
    });

    verticies.forEach(vertex => {
        let node = nodes.filter(x => x.identifier === vertex.identifier);

        if (node.length > 0) {
            node[0].connectedNodes.forEach(connectedNode => {
                let connectedVertex = verticies.filter(x => x.identifier === connectedNode.identifier);
                if (connectedVertex.length > 0) {
                    vertex.adj.push(connectedVertex[0]);
                } else {
                    console.error("No vertex found");
                }
            });
        } else {
            console.error("No node found.");
        }
    });

    return verticies;
}

function transferBackToNodes(nodes, vertexLoops) {
    let loops = [];
    vertexLoops.forEach(vertexLoop => {
        let nodeLoop = [];
        vertexLoop.forEach(vertex => {
            let node = nodes.filter(x => x.identifier === vertex.identifier);
            if (node.length > 0) {
                nodeLoop.push(node[0]);
            } else {
                console.error("No node found. Transfer back");
            }
        });

        if (nodeLoop.length > 0) {
            loops.push(nodeLoop);
        } else {
            console.error("invalid loop");
        }
    });

    return loops;
}

//CREATING ROOM MESHES
//Erzeugen der Flächen für die 3D-Ansicht aus den gefundenen Loops.
function createRoomMeshes(loops) {
    let roomMeshes = [];

    let shapesPoints = [];
    for (const loop of loops) {
        let points = loop.map(node => ({x: node.point.x, z: node.point.z}));

        if (!pathPointsClockwise(points)) {
            points = points.reverse();
        }
        shapesPoints.push(points);
    }

    for (let i = 0; i < shapesPoints.length; i++) {
        let points = offsetContour(0.05, convertToVector2Array(shapesPoints[i]));
        let area = ShapeUtils.area(points);
        points = convertToVector3Array(points);
        let shape = createShapeFromPoints(points);
        let mesh = createMeshFromShape(shape, {depth: 0, bevelEnabled: false});

        mesh.userData.area = area;
        mesh.userData.points = points;

        if (!isDebug) {
            mesh.material = roomMaterial;
        } else {
            mesh.material = new MeshStandardMaterial({
                color: new Color(Math.random() * 0xffffff),
                side: DoubleSide,
                opacity: 0.4,
                transparent: true
            });
        }


        roomMeshes.push(mesh);
    }

    return roomMeshes;
}

function convertToVector2Array(points) {
    return points.map(point => new Vector2(point.x, point.z));
}

export function convertToVector3Array(vector2Array, yOffset = 0) {
    return vector2Array.map(point => new Vector3(point.x, yOffset, point.y));
}

//Versetzen der Punkte um von Mittellinie der Wand auf die inneren Kanten zu kommen
function offsetContour(offset, contour) {
    let result = [];
    offset = new BufferAttribute(new Float32Array([offset, 0, 0]), 3);

    for (let i = 0; i < contour.length; i++) {
        let v1 = new Vector2().subVectors(contour[i - 1 < 0 ? contour.length - 1 : i - 1], contour[i]);
        let v2 = new Vector2().subVectors(contour[i + 1 == contour.length ? 0 : i + 1], contour[i]);
        let angle = v2.angle() - v1.angle();
        let hA = angle * 0.5;
        let tA = v2.angle() + Math.PI * 0.5;

        let shift = Math.tan(hA - Math.PI * 0.5);
        let shiftMatrix = new Matrix4().set(
            1, 0, 0, 0,
            -shift, 1, 0, 0,
            0, 0, 1, 0,
            0, 0, 0, 1
        );

        let tempAngle = tA;
        let rotationMatrix = new Matrix4().set(
            Math.cos(tempAngle), -Math.sin(tempAngle), 0, 0,
            Math.sin(tempAngle), Math.cos(tempAngle), 0, 0,
            0, 0, 1, 0,
            0, 0, 0, 1
        );

        let translationMatrix = new Matrix4().set(
            1, 0, 0, contour[i].x,
            0, 1, 0, contour[i].y,
            0, 0, 1, 0,
            0, 0, 0, 1
        );

        let cloneOffset = offset.clone();
        cloneOffset.applyMatrix4(shiftMatrix);
        cloneOffset.applyMatrix4(rotationMatrix);
        cloneOffset.applyMatrix4(translationMatrix);

        result.push(new Vector2(cloneOffset.getX(0), cloneOffset.getY(0)));
    }

    return result;
}

//DEBUG
function debugDrawNodes(connectedNodes) {
    if (!isDebug) {
        return;
    }

    clearGroup(roomNamesGroup);
    connectedNodes.forEach(node => {
        if (isDebug) {
            let point = node.point;
            point.y = 4;
            roomNamesGroup.addWithUuid(addNumberedDefaultCirclePointer(point, node.identifier));
        }
    });
}
